import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/auth/AuthComponents';
import { apiRequest } from '../utils/api';
import { toast } from 'react-toastify';
import AdminDashboard from '../components/admin/AdminDashboard';
import ConfirmModal from '../components/shared/ConfirmModal';
import { isAdminRole } from '../utils/userPermissions';

const AdminPage = ({ setCurrentPage }) => {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState({
    stats: {},
    users: [],
    plans: [],
    payments: [],
    activities: [],
  });
  const [loading, setLoading] = useState(true);
  const [userToDelete, setUserToDelete] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdminRole(user?.role)) return;
    fetchAdminData();
  }, [authLoading, user?.role]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const [stats, users, plans, payments, activities] = await Promise.all([
        apiRequest('admin/stats').catch(() => ({})),
        apiRequest('admin/users').catch(() => ({ users: [] })),
        apiRequest('subscription/plans').catch(() => ({ plans: [] })), // Assuming plans are public
        apiRequest('admin/payments').catch(() => ({ payments: [] })),
        apiRequest('admin/activity-logs').catch(() => ({ logs: [] })),
      ]);
      setData({
        stats,
        users: users.users,
        plans: plans.plans,
        payments: payments.payments,
        activities: activities.logs,
      });
    } catch (error) {
      toast.error(error.message || 'Failed to fetch admin data.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteUser = async (userId) => {
    try {
      await apiRequest(`admin/users/${userId}`, { method: 'DELETE' });
      toast.success('User deleted successfully.');
      fetchAdminData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete user.');
    }
  };

  const handleSavePlan = async (planData, planId) => {
    const isEditing = !!planId;
    const url = isEditing ? `admin/subscription-plans/${planId}` : 'admin/subscription-plans';
    const method = isEditing ? 'PUT' : 'POST';
    
    try {
      await apiRequest(url, { method, body: planData });
      toast.success(`Plan ${isEditing ? 'updated' : 'created'} successfully.`);
      fetchAdminData();
      return true;
    } catch (error) {
      toast.error(`Failed to save plan: ${error.message}`);
      return false;
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await apiRequest(`admin/subscription-plans/${planId}`, { method: 'DELETE' });
      toast.success('Plan deleted successfully.');
      fetchAdminData();
    } catch (error) {
      toast.error(`Failed to delete plan: ${error.message}`);
    }
  };

  if (authLoading || (isAdminRole(user?.role) && loading)) {
    return <div className="text-center p-10">Loading admin dashboard...</div>;
  }
  
  if (!isAdminRole(user?.role)) {
    return (
      <div className="text-center p-10">
        <h1 className="text-2xl font-bold text-red-500">Access Denied</h1>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <>
    <AdminDashboard
      stats={data.stats}
      users={data.users}
      plans={data.plans}
      payments={data.payments}
      activities={data.activities}
      onDeleteUser={(userId) => setUserToDelete(userId)}
      onSavePlan={handleSavePlan}
      onDeletePlan={(planId) => setPlanToDelete(planId)}
    />
    <ConfirmModal
      open={userToDelete != null}
      title="Delete user?"
      message="Are you sure you want to delete this user?"
      confirmLabel="Delete"
      cancelLabel="Cancel"
      destructive
      onCancel={() => setUserToDelete(null)}
      onConfirm={async () => {
        if (userToDelete == null) return;
        await handleDeleteUser(userToDelete);
        setUserToDelete(null);
      }}
    />
    <ConfirmModal
      open={planToDelete != null}
      title="Delete plan?"
      message="Are you sure you want to delete this plan?"
      confirmLabel="Delete"
      cancelLabel="Cancel"
      destructive
      onCancel={() => setPlanToDelete(null)}
      onConfirm={async () => {
        if (planToDelete == null) return;
        await handleDeletePlan(planToDelete);
        setPlanToDelete(null);
      }}
    />
    </>
  );
};

export default AdminPage;
